<template>
  <div class="accommodation-filter">
    <div class="accommodation-filter">
      <UiCheckbox
        v-for="item in visibleCheckboxItems"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :checked="item.checked"
        class="accommodation-filter__checkbox"
        @input="toggleValue(item.value)"
      >
        <template #hint>
          {{ item.description }}
        </template>
      </UiCheckbox>

      <UiButton
        v-show="hasMore && !isExpanded"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.ACCENT"
        :text="t('hasMore')"
        type="button"
        class="accommodation-filter__more-btn"
        @click="isExpanded = !isExpanded"
      >
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { watch, ref, computed } from 'vue';
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { UiCheckbox } from '@experience/core-checkbox-ui';
  import { getAccommodationFilterItems } from './consts';
  import { type TAccommodationValue } from '@/features/filter/accommodation';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import { useCategoryStore } from '@/shared/store';
  import { EExperienceFilterType } from '@/widgets/filtersBlock';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const categoryStore = useCategoryStore();

  const INITIAL_VISIBLE_COUNT = 8;
  const isExpanded = ref(false);
  const hasMore = computed(() => checkboxItems.value.length > INITIAL_VISIBLE_COUNT);

  const {
    HOTEL_3,
    HOTEL_4_5,
    APARTMENTS,
    GUEST_HOUSE,
    COTTAGE,
    CAMPING,
    GLAMPING,
    HOTEL_2,
    SPA_HOTEL,
    UNIQUE,
    YACHT_SHIP,
    TRAIN,
    NOT_SET,
  } = getAccommodationFilterItems();

  const accommodationItemsList = [
    HOTEL_3,
    HOTEL_4_5,
    APARTMENTS,
    GUEST_HOUSE,
    COTTAGE,
    CAMPING,
    GLAMPING,
    HOTEL_2,
    SPA_HOTEL,
    UNIQUE,
    YACHT_SHIP,
    TRAIN,
    NOT_SET,
  ];

  const toAtoms = (values: readonly (string | TAccommodationValue)[]) => {
    if (typeof values === 'string') {
      return values;
    }

    return values.flatMap((v) =>
      String(v)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  };

  const selectedAtoms = ref<Set<string>>(
    new Set(toAtoms(categoryStore.tmpFilters[EExperienceFilterType.ACCOMMODATION].accommodation) as string[]),
  );

  const toggleValue = (groupValue: TAccommodationValue) => {
    const atoms = String(groupValue)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const allSelected = atoms.every((a) => selectedAtoms.value.has(a));

    if (allSelected) {
      atoms.forEach((a) => selectedAtoms.value.delete(a));
    } else {
      atoms.forEach((a) => selectedAtoms.value.add(a));
    }

    categoryStore.setAccommodation(Array.from(selectedAtoms.value) as TAccommodationValue[]);
  };

  const checkboxItems = computed(() =>
    accommodationItemsList.map((item) => {
      const atoms = String(item.value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const checked = atoms.every((a) => selectedAtoms.value.has(a));

      return { ...item, checked };
    }),
  );

  const visibleCheckboxItems = computed(() =>
    isExpanded.value ? checkboxItems.value : checkboxItems.value.slice(0, INITIAL_VISIBLE_COUNT),
  );

  watch(
    () => categoryStore.tmpFilters[EExperienceFilterType.ACCOMMODATION].accommodation,
    (next) => {
      selectedAtoms.value = new Set(toAtoms(next as string[]));
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  .accommodation-filter {
    margin-top: 8px;
  }

  .accommodation-filter__checkbox {
    grid-template-columns: 20px 1fr;
    width: 100%;
    margin: 8px 0;
  }

  .accommodation-filter__more-btn {
    --btn-text-color: var(--comp-button-ghost-color-text-accent);
    margin-left: -24px;
    color: var(--comp-button-ghost-color-text-accent) !important;
  }
</style>
